/*
 * Copyright (c) 2020, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React from 'react';
import { connect } from 'react-redux';

import { makeStyles, Grid, Typography, Avatar, Divider } from '@material-ui/core';

import { isValidData, renderSkeleton } from '../../data/utils';
import { ApiReducer, ApplicationState, RatingsReducer } from '../../data/interfaces';
import ApiRating from "../api/ApiOverview/ApiRating";
import { FormattedMessage } from 'react-intl';
import Settings from '../../conf/settings';

const useStyles = makeStyles((theme) => ({
    root: {
        flexGrow: 1,
        margin: '10px 40px 10px 40px',
        width: 'auto',
    },
    apiIconImage: {
        margin: 'auto',
        marginLeft: 0,
        display: 'flex',
        maxWidth: '100%',
        maxHeight: '100%',
        width: 49,
        height: 49,
        minWidth: 49,
        minHeight: 49,
        ...theme.custom.elements.apiInfoBar.apiIconImage
    },
    apiIconImageBg: {
        ...theme.custom.elements.apiInfoBar.apiIconImageBackground
    },
    gridText: {
        textAlign: 'left',
        maxWidth: 'fit-content',
        marginLeft: -theme.spacing(0.8)
    },
    apiName: {
        height: '24px',
        width: 'auto',
        ...theme.custom.elements.apiInfoBar.apiName
    },
    apiVersionProvider: {
        height: '24px',
        width: 'auto',
        ...theme.custom.elements.apiInfoBar.apiVersionProvider
    },
    apiRatingText: {
        height: '24px',
        width: 'auto',
        ...theme.custom.elements.apiInfoBar.apiRatingText
    },
    gridStyles: {
        maxWidth: 'fit-content',
    },
    divider: {
        marginLeft: theme.spacing(7),
        marginRight: theme.spacing(7),
        marginTop: -theme.spacing(0.8),
        ...theme.custom.elements.apiInfoBar.divider
    }
}));

interface ApiInfoBarProps {
    apiReducer: ApiReducer,
    ratingsReducer: RatingsReducer
}

function ApiInfoBar({ apiReducer, ratingsReducer }: ApiInfoBarProps) {

    const classes = useStyles();
    const { loading: apiLoading, api, apiThumbnail } = apiReducer;
    const { ratings } = ratingsReducer;

    let apiName = '';
    let apiVersion = '';
    let apiOwner = '';
    let thumbnail = undefined;
    let avgRating = '';
    let isLatestMajorRangeVersion = false;

    if (isValidData(api)) {
        apiName = api.displayName;
        apiVersion = api.version;
        avgRating = api.avgRating;
        apiOwner = api.businessInformation?.businessOwner || api.businessInformation?.technicalOwner;
        isLatestMajorRangeVersion = api?.versionInfo?.majorRange?.isLatest || false;
    }

    if (isValidData(ratings)) {
        avgRating = ratings.avgRating;
    }

    if (apiThumbnail) {
        thumbnail = URL.createObjectURL(apiThumbnail);
    }

    return (
        <div>
            <Grid container spacing={3} direction='row' justifyContent='space-between' className={classes.root}>
                {
                    apiLoading || !apiThumbnail ?
                        <Grid item xs={12} lg={2} style={{ maxWidth: 'fit-content' }}>
                            {renderSkeleton("rect", 49, 49)}
                        </Grid>
                        :
                        <Grid item xs={12} lg={2} className={classes.gridStyles}>
                            <Avatar
                                className={classes.apiIconImage}
                                classes={{ img: classes.apiIconImageBg }}
                                alt={apiName}
                                src={thumbnail}
                                data-testid="thumbnail"
                            />
                        </Grid>
                }
                <Grid item sm={true}>
                    <Grid
                        item
                        xs={9}
                        sm={9}
                        lg={7}
                        className={classes.gridText}
                        container
                        direction="column"
                        style={{ paddingRight: '60px' }}
                    >
                        {
                            apiLoading ?
                                <Typography className={classes.apiName}>
                                    {renderSkeleton("rect", 100, 20)}
                                </Typography>
                                :
                                <Typography className={classes.apiName}>
                                    {apiName}
                                </Typography>
                        }

                        {
                            apiLoading ?
                                <Typography className={classes.apiVersionProvider}>
                                    {renderSkeleton("rect", 100, 20)}
                                </Typography>
                                :
                                <Typography className={classes.apiVersionProvider}>
                                    <FormattedMessage
                                        id='Apis.Details.components.ApiInfoBar.version'
                                        defaultMessage='Version {apiVersion}'
                                        values={{
                                            apiVersion:
                                                Settings.intelligentRoutingFeatureEnabled && isLatestMajorRangeVersion
                                                    ? apiVersion.split(".")[0]
                                                    : apiVersion
                                        }}
                                    />
                                    &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;
                                    {apiOwner ? "by " + apiOwner : <i>No owner available.</i>}
                                </Typography>
                        }
                    </Grid>
                </Grid>
                <Grid item xs={9} sm={8} lg={4} className={classes.gridStyles}>
                    {
                        apiLoading ?
                            renderSkeleton("rect", 280, 49)
                            :
                            <ApiRating hideTryOut={true} />
                    }
                </Grid>
            </Grid>
            <Divider variant="middle" className={classes.divider} />
        </div>
    );
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        apiReducer: state.apiReducer,
        ratingsReducer: state.ratingsReducer,
    }
}

export default connect(mapStateToProps)(ApiInfoBar);
